import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectPostById, setEditId } from "src/store/community/postsSlice";
import { destroyPost, fetchPost, toggleShow } from "src/actions/community/postsActions";
import communityUrls from "src/configs/community/communityUrls";
import { fetchCategories } from "src/actions/community/categoriesActions";

import {
  LikeOutlined,
  HeartOutlined,
  MessageOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { message } from "antd";
import Tabs from "src/componets/Tabs";
import PageLayout from "src/pages/community/_partials/PageLayout";
import IconText from "src/componets/IconText";
import StatsIcons from "src/componets/StatsIcons";
import Edit from "./Edit";
import SummaryShow from "src/componets/SummaryShow";
import SidebarShow from "./_partials/_SidebarShow";
import Details from "./_partials/_Details";
import { ButtonDestroy, ButtonDownload, ButtonEdit, ButtonToggleShow } from "src/componets/Buttons";
import Comments from "./_partials/_Comments";
import EditComment from "src/pages/community/Comments/Edit";
import {HiddenTag} from "../../../componets/Tags/HiddenTag";
import usePermissions from "src/hooks/usePermissions";

const Show: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const post = useAppSelector(state => id ? selectPostById(state, id) : undefined);
  const { write_community } = usePermissions();

  useEffect(() => {
    if (id) {
      dispatch(fetchPost(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (post) {
      dispatch(fetchCategories({ params: { app_id: post.app_id, target: post.target } }));
    }
  }, [post, dispatch]);

  function onClickEdit() {
    dispatch(setEditId(id));
  }

  function onClickDestroy() {
    handleDestroy(id).catch(console.error);
  }

  function onToggleShow(id: any) {
    if (!post) return;

    dispatch(toggleShow({ id, published: post.published }));
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyPost(id));

    if (destroyPost.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      const url = post?.target === 'content' ? communityUrls.contentPath() : communityUrls.communityPath();
      setTimeout(() => navigate(url), 1000);
    }

    if (destroyPost.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  function StatusTag() {
    if (!post) return null;

    return <HiddenTag key={`show-tag-${post.id}`} hidden={!post.published} />
  }

  return (
    <PageLayout className='v-posts-show'>
      <div className='v-container-fluid'>
        <div className='o-header'>
          {
            post &&
              <SummaryShow
                title={post.title}
                subtitle={<StatusTag />}
                image={post.thumbnail_url}
                createdAt={post.created_at}
                updatedAt={post.updated_at}
                publishedAt={post.published_at}
                className='v-mb-15'
                actions={[
                  ...(post.file_url ? [<ButtonDownload key='button-download' href={post.file_url} />] : []),
                  ...(write_community ? [
                    <ButtonToggleShow
                      key={`show-${post.id}`}
                      onClick={() => onToggleShow(post.id)}
                      state={post.published}
                    />,
                    <ButtonEdit key='button-edit' onClick={onClickEdit} />,
                    <ButtonDestroy key='button-destroy' onClick={onClickDestroy} />,
                  ] : [])
                ]}
              />
          }

          <StatsIcons
            icons={[
              <IconText tooltip='popular' icon={HeartOutlined} text={post?.popular_count} />,
              <IconText tooltip='likes' icon={LikeOutlined} text={post?.likes_count} />,
              <IconText tooltip='comments' icon={MessageOutlined} text={post?.comments_count} />,
              <IconText tooltip='downloads' icon={DownloadOutlined} text={post?.downloads_count} />
            ]}
          />
        </div>

        <Tabs
          paramsKey='tab'
          defaultActiveKey='stickers'
          items={[
            { label: 'Details', key: 'details', children: <Details post={post} /> },
            { label: 'Comments', key: 'comment', children: <Comments postId={post?.id} /> }
          ]}
        />
      </div>

      <SidebarShow id={id} />

      <Edit />

      <EditComment />
    </PageLayout>
  )
}

export default Show;
